import { Router } from 'vue-router'

import { useAuthStore } from '@/store/auth'
import { routeNames } from '@/router/route-names'
import { hasEvAccess, hasGolfAccess, hasP2pAccess } from '@/helpers/rbac'

type Guard = Parameters<Router['beforeEach']>[0]

const rbacGuard: Guard = to => {
  const authStore = useAuthStore()

  const userScopes = authStore.scopes
  const isAuthenticated = authStore.isAuthenticated
  const currentUser = authStore.session?.user

  if (!isAuthenticated || !currentUser) {
    return true
  }

  const path = (to.path || '').toLowerCase()
  const name = typeof to.name === 'string' ? to.name.toLowerCase() : ''

  const isEvRoute =
    path.startsWith('/ev-') ||
    path.startsWith('/ev/') ||
    path.includes('/create-ev') ||
    path === '/ev' ||
    name.startsWith('ev') ||
    name.includes('evcar')
  const isGolfRoute =
    path.startsWith('/golf-') ||
    path.startsWith('/golf/') ||
    name.startsWith('golf')
  const isGuestOrCommon =
    name === 'login' ||
    name === 'about' ||
    name === 'notfound' ||
    to.name === routeNames.notFound

  // 1. Strict Module-level Slug / URL Protection
  if (isEvRoute && !hasEvAccess(currentUser)) {
    return { name: routeNames.notFound, replace: true }
  }

  if (isGolfRoute && !hasGolfAccess(currentUser)) {
    return { name: routeNames.notFound, replace: true }
  }

  if (
    !isEvRoute &&
    !isGolfRoute &&
    !isGuestOrCommon &&
    !hasP2pAccess(currentUser)
  ) {
    return { name: routeNames.notFound, replace: true }
  }

  // 2. Super admin / admin role bypass
  if (currentUser.role === 'super_admin' || currentUser.role === 'admin') {
    return true
  }

  // 3. Route-level scopes check (RBAC)
  const routeScopes = to.meta.scopes as undefined | string[]

  // Skip if route doesn't have rbac rules
  if (!Array.isArray(routeScopes) || !routeScopes.length) {
    return true
  }

  // Check if user has required scopes
  if (routeScopes.every(s => userScopes.includes(s))) {
    return true
  }

  return { name: routeNames.notFound, replace: true }
}

export default rbacGuard
